import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Prod01 } from '../../constants/ActionType';
import { useSortableTable, SortableTh } from '../SortableTable';

const RATE_SORT_COLUMNS = {
    code: { type: 'string', value: (r) => r?.code },
    scope: { type: 'string', value: (r) => r?.scope },
    region: { type: 'string', value: (r) => r?.region || r?.organizationId },
    effective: { type: 'date', value: (r) => r?.effectiveFrom },
    amount: { type: 'number', value: (r) => r?.amount },
    payer: { type: 'string', value: (r) => r?.payer },
    setBy: { type: 'name', value: (r) => r?.setByName },
};

const SCOPES = ['national', 'region', 'org'];
const money = (n) => (typeof n === 'number' ? `$${n.toFixed(2)}` : 'pending');

/**
 * Billing-editable, effective-dated allowed amounts (manual entry only, no CMS pull).
 * Precedence for a patient's org: org/contract > region > national.
 */
const BillingRatesPanel = ({ organizationId, onChanged }) => {
    const { user } = useSelector(state => state.auth);
    const canWrite = ['billing', 'admin'].includes(user?.role);
    const [codes, setCodes] = useState([]);
    const [rates, setRates] = useState([]);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [form, setForm] = useState({ code: '99490', scope: 'org', region: '', organizationId: organizationId || '', effectiveFrom: '2026-01-01', effectiveTo: '', amount: '', payer: 'Medicare', note: '' });

    const tableSort = useSortableTable(rates, { id: 'billing.rates', columns: RATE_SORT_COLUMNS, defaultSort: { key: 'code', dir: 'asc' } });
    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${Prod01}/billing/rates`);
            setCodes(res.data.codes || []);
            setRates(res.data.rates || []);
        } catch (e) { setError(e?.response?.data?.message || e.message); }
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { setForm(f => ({ ...f, organizationId: organizationId || f.organizationId })); }, [organizationId]);

    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        setError(null); setNotice(null);
        try {
            const body = { ...form, amount: form.amount === '' ? null : Number(form.amount), effectiveTo: form.effectiveTo || null };
            if (body.scope !== 'region') delete body.region;
            if (body.scope !== 'org') delete body.organizationId;
            await axios.post(`${Prod01}/billing/rates`, body);
            setNotice(`${form.code} ${form.scope} rate saved.`);
            await load(); if (onChanged) onChanged();
        } catch (e) { setError(e?.response?.data?.message || e.message); }
    };
    const remove = async (id) => {
        setError(null);
        try { await axios.delete(`${Prod01}/billing/rates/${id}`); await load(); if (onChanged) onChanged(); } catch (e) { setError(e?.response?.data?.message || e.message); }
    };

    return (
        <div className="p-2 mt-1 rounded" style={{ border: '1px solid #dee2e6', fontSize: 12 }}>
            <div className="fw-bold">Allowed amounts (effective-dated). Most specific wins: contract/org → region → national.</div>
            <div className="text-muted">National 2026 defaults were loaded from billing. Type your region or contract allowed amount per code; leave amount blank to mark pending.</div>
            {error && <div className="alert alert-danger py-1 my-1">{error}</div>}
            {notice && <div className="alert alert-success py-1 my-1">{notice}</div>}
            <table className="table table-sm mb-1" style={{ fontSize: 12 }}>
                <thead><tr><SortableTh sortKey="code" sort={tableSort}>Code</SortableTh><SortableTh sortKey="scope" sort={tableSort}>Scope</SortableTh>
                    <SortableTh sortKey="region" sort={tableSort}>Region / Org</SortableTh><SortableTh sortKey="effective" sort={tableSort}>Effective</SortableTh>
                    <SortableTh sortKey="amount" sort={tableSort}>Amount</SortableTh><SortableTh sortKey="payer" sort={tableSort}>Payer</SortableTh>
                    <SortableTh sortKey="setBy" sort={tableSort}>Set by</SortableTh><th /></tr></thead>
                <tbody>
                    {tableSort.rows.map(r => (
                        <tr key={r._id || r.id}>
                            <td>{r.code}</td><td>{r.scope}</td>
                            <td>{r.scope === 'region' ? r.region : r.scope === 'org' ? String(r.organizationId || '').slice(-6) : '—'}</td>
                            <td>{r.effectiveFrom}{r.effectiveTo ? ` → ${r.effectiveTo}` : ' →'}</td>
                            <td>{money(r.amount)}</td><td>{r.payer}</td><td>{r.setByName || ''}</td>
                            <td>{canWrite && r.scope !== 'national' && <button className="btn btn-link btn-sm p-0" style={{ fontSize: 11 }} onClick={() => remove(r._id || r.id)}>delete</button>}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {canWrite && (
                <div className="d-flex flex-wrap gap-1 align-items-center">
                    <select className="form-select form-select-sm" style={{ width: 100 }} value={form.code} onChange={set('code')}>{codes.map(c => <option key={c}>{c}</option>)}</select>
                    <select className="form-select form-select-sm" style={{ width: 110 }} value={form.scope} onChange={set('scope')}>{SCOPES.map(sc => <option key={sc}>{sc}</option>)}</select>
                    {form.scope === 'region' && <input className="form-control form-control-sm" style={{ width: 110 }} placeholder="Region e.g. TX-31" value={form.region} onChange={set('region')} />}
                    {form.scope === 'org' && <input className="form-control form-control-sm" style={{ width: 220 }} placeholder="organizationId" value={form.organizationId} onChange={set('organizationId')} />}
                    <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={form.effectiveFrom} onChange={set('effectiveFrom')} />
                    <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={form.effectiveTo} onChange={set('effectiveTo')} title="effective to (optional)" />
                    <input type="number" step="0.01" min="0" className="form-control form-control-sm" style={{ width: 110 }} placeholder="Amount $" value={form.amount} onChange={set('amount')} />
                    <input className="form-control form-control-sm" style={{ width: 120 }} placeholder="Payer" value={form.payer} onChange={set('payer')} />
                    <input className="form-control form-control-sm" style={{ width: 180 }} placeholder="Note / contract ref" value={form.note} onChange={set('note')} />
                    <button className="btn btn-outline-primary btn-sm" onClick={save}>Save rate</button>
                </div>
            )}
        </div>
    );
};

export default BillingRatesPanel;
